import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { FaFileExcel, FaFilter, FaUsers, FaUserCheck, FaUserClock, FaSearch } from "react-icons/fa";

function VendorReport() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllVendors`);
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error("Fetch vendors error:", err);
      Swal.fire("Error", "Failed to fetch vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const filteredVendors = vendors.filter((v) => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch =
      v.name.toLowerCase().includes(searchLower) ||
      v.email.toLowerCase().includes(searchLower) ||
      v.phone.toLowerCase().includes(searchLower) ||
      v.business_name.toLowerCase().includes(searchLower);

    const matchesStatus = filters.status ? v.status === filters.status : true;
    const matchesFromDate = filters.fromDate ? new Date(v.created_at) >= new Date(filters.fromDate) : true;
    const matchesToDate = filters.toDate ? new Date(v.created_at) <= new Date(filters.toDate) : true;

    return matchesSearch && matchesStatus && matchesFromDate && matchesToDate;
  });

  const exportToExcel = () => {
    if (!filteredVendors.length) {
      Swal.fire("Info", "No data available to export", "info");
      return;
    }

    const data = filteredVendors.map((v) => ({
      "Merchant Name": v.name,
      "Official Email": v.email,
      "Contact Number": v.phone,
      "Business Entity": v.business_name,
      "Account Status": v.status.toUpperCase(),
      "Registration Date": new Date(v.created_at).toLocaleDateString("en-GB"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendor_Database");
    XLSX.writeFile(workbook, `Vendor_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h4 className="fw-bold mb-1">Vendor Intelligence Report</h4>
              <p className="text-muted small mb-0">Review merchant registration trends, account statuses, and export database subsets for offline auditing.</p>
            </div>
            <button className="btn btn-success shadow-sm d-flex align-items-center gap-2 px-3 border-0" onClick={exportToExcel} style={{backgroundColor: '#1d6f42'}}>
              <FaFileExcel /> Export XLSX
            </button>
          </div>

          {/* Quick Summary Cards */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3 rounded-4 bg-white">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-primary bg-opacity-10 p-2 rounded-3 text-primary"><FaUsers size={20}/></div>
                  <div>
                    <small className="text-muted d-block fw-bold text-uppercase" style={{fontSize: '10px'}}>Total Partners</small>
                    <h5 className="mb-0 fw-bold">{vendors.length}</h5>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3 rounded-4 bg-white border-start border-success border-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="text-success"><FaUserCheck size={20}/></div>
                  <div>
                    <small className="text-muted d-block fw-bold text-uppercase" style={{fontSize: '10px'}}>Active Merchants</small>
                    <h5 className="mb-0 fw-bold text-success">{vendors.filter(v => v.status === 'active').length}</h5>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3 rounded-4 bg-white border-start border-danger border-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="text-danger"><FaUserClock size={20}/></div>
                  <div>
                    <small className="text-muted d-block fw-bold text-uppercase" style={{fontSize: '10px'}}>Inactive/Suspended</small>
                    <h5 className="mb-0 fw-bold text-danger">{vendors.filter(v => v.status !== 'active').length}</h5>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3 rounded-4 bg-white">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-dark bg-opacity-10 p-2 rounded-3 text-dark"><FaFilter size={20}/></div>
                  <div>
                    <small className="text-muted d-block fw-bold text-uppercase" style={{fontSize: '10px'}}>Filtered Results</small>
                    <h5 className="mb-0 fw-bold">{filteredVendors.length}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="card border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
            <div className="card-body p-3 bg-white">
              <div className="row g-2 align-items-end">
                <div className="col-md-4">
                  <label className="extra-small fw-bold text-muted mb-1 text-uppercase">Keyword Search</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-0"><FaSearch className="text-muted" /></span>
                    <input
                      type="text"
                      className="form-control bg-light border-0"
                      placeholder="Name, Email, Phone..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="extra-small fw-bold text-muted mb-1 text-uppercase">Account Status</label>
                  <select
                    className="form-select form-select-sm bg-light border-0"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="extra-small fw-bold text-muted mb-1 text-uppercase">Registration Period</label>
                  <div className="d-flex gap-2">
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      placeholder="Start Date"
                      className="form-control form-control-sm bg-light border-0 text-center"
                      value={filters.fromDate}
                      onChange={(date) => setFilters({ ...filters, fromDate: date[0] })}
                    />
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      placeholder="End Date"
                      className="form-control form-control-sm bg-light border-0 text-center"
                      value={filters.toDate}
                      onChange={(date) => setFilters({ ...filters, toDate: date[0] })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-sm btn-outline-secondary w-100 border-0 fw-bold" onClick={() => setFilters({search: "", status: "", fromDate: "", toDate: ""})}>
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Table */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0 vendor-report-table">
                <thead className="bg-white border-bottom">
                  <tr>
                    <th className="ps-4">#</th>
                    <th>Merchant Identity</th>
                    <th>Contact Details</th>
                    <th>Business Entity</th>
                    <th>Status</th>
                    <th className="pe-4 text-end">Joined Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : filteredVendors.length > 0 ? (
                    filteredVendors.map((v, i) => (
                      <tr key={v.id}>
                        <td className="ps-4 text-muted small">{i + 1}</td>
                        <td>
                          <div className="fw-bold text-dark small">{v.name}</div>
                          <div className="extra-small text-muted">ID: #VND-{v.id}</div>
                        </td>
                        <td>
                          <div className="small text-dark">{v.email}</div>
                          <div className="extra-small text-muted">{v.phone}</div>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark fw-normal border px-2 py-1">{v.business_name}</span>
                        </td>
                        <td>
                          <span className={`status-dot ${v.status === "active" ? "bg-success" : "bg-danger"}`}></span>
                          <span className="small ms-2 text-capitalize">{v.status}</span>
                        </td>
                        <td className="pe-4 text-end">
                          <div className="small fw-bold text-muted">
                            {new Date(v.created_at).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted small">
                        No vendor records matched your specified criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="card-footer bg-white py-3 border-0">
               <div className="d-flex justify-content-between align-items-center">
                 <small className="text-muted italic">Showing {filteredVendors.length} of {vendors.length} total entries.</small>
               </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; line-height: 1.2; }
        // .vendor-report-table thead th { font-size: 11px; text-transform: uppercase; color: #8898aa; letter-spacing: 0.5px; padding: 15px 10px; }
        .vendor-report-table tbody td { padding: 12px 10px; border-bottom: 1px solid #f6f9fc; }
        
        .status-dot { height: 8px; width: 8px; border-radius: 50%; display: inline-block; }
        .rounded-4 { border-radius: 1rem !important; }
        .italic { font-style: italic; }
      `}</style>
    </>
  );
}

export default VendorReport;